/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  ScrollView,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import ModalFull from '@components/modal/ModalFull';
import ModalImage from '@components/modal/ModalImage';
import { showImage } from '@/store/global';
import Colors from '@global/colors';
import {
  isWeb,
  isMobileWeb,
  isNative,
  fullWidth,
  fullHeight,
} from '@global/screens';
import Image from '@components/global/Image';
import ItemDetail from '@components/global/ItemDetail';
import TitleSubSection from '@components/global/TitleSubSection';
import SociollaText from '@components/sociolla/SociollaText';
import SliderImage from '@components/slider/SliderImage';
import StatusBadge from '@components/global/StatusBadge';
import ProductBadge from '@components/global/ProductBadge';
import { Loader } from '@components/screen/Layout';

import { parseDate, currency } from '@global/helpers';
import { MsCatalog } from '@api/ms-catalog';

import PropTypes from 'prop-types';

const propTypes = {
  show: PropTypes.bool,
  withPromotion: PropTypes.bool,
  title: PropTypes.string,
  back: PropTypes.bool,
  onClose: PropTypes.func,
};

const defaultProps = {
  show: false,
  withPromotion: false,
  title: '',
  back: false,
  onClose: () => {},
};

const imagePopupSize = 130;
const containerWidth = isWeb ? 350 : fullWidth;

const getImageUrl = (arr) => {
  if (arr && arr.length) {
    const findData = arr.filter((a) => a.url);
    if (findData && findData.length) {
      return findData.map((a) => a.url);
    }
  }
  return [''];
};

// eslint-disable-next-line react/prop-types
const ModalDetailProduct = ({
  show,
  withPromotion = false,
  title = '',
  back = false,
  onClose,
}) => {
  const dispatch = useDispatch();
  const productData = useSelector((state) => state.product.productData);
  const promotionTypes = useSelector((state) => state.global.promotionTypes);
  const detail = productData || {};

  const [selectedImageCombination, setSelectedImageCombination] = useState([
    '',
  ]);
  const [loadingPromotion, setLoadingPromotion] = useState(false);
  const [reference, setReference] = useState('');
  const [price, setPrice] = useState(0);
  const [tags, setTags] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [comObj, setComObj] = useState({});
  const [sizes, setSizes] = useState([]);
  const [shades, setShades] = useState([]);
  const [variants, setVariants] = useState([]);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    if (show) {
      setSizes([]);
      setShades([]);
      setVariants([]);
      setComObj({});
      setReference('');
      setPrice(0);
      setTags([]);
      if (detail) {
        getProductReference(detail);
        getProductPrice(detail);
        getProductTags(detail);
        if (detail?.image) {
          setSelectedImageCombination([detail.image]);
        } else {
          if (detail?.images?.length) {
            setSelectedImageCombination(getImageUrl(detail.images));
          }
        }
        if (withPromotion) {
          let combinationId = '';
          if (detail?.combinations?.[0]?._id) {
            combinationId = detail.combinations[0]._id;
          } else if (detail?.combination?.id) {
            combinationId = detail.combination.id;
          } else if (detail?.combination?._id) {
            combinationId = detail.combination._id;
          } else if (detail?.combination_id) {
            combinationId = detail.combination_id;
          } else if (detail?.product_combination_id) {
            combinationId = detail.product_combination_id;
          }
          if (detail?.id) {
            getProductPromotions(detail?.id, combinationId);
          }
        }
      }
    }
  }, [show]);

  const getProductReference = (data) => {
    if (data?.default_combination?.reference) {
      setReference(data.default_combination.reference);
    } else if (data?.combinations?.length && data.combinations[0].reference) {
      setReference(data.combinations[0].reference);
    } else if (data?.combination?.reference) {
      setReference(data.combination.reference);
    }
  };

  const getProductPrice = (data) => {
    if (data?.price && !isNaN(data?.price)) {
      setPrice(data.price);
    } else if (
      data?.default_combination?.price &&
      !isNaN(data?.default_combination?.price)
    ) {
      setPrice(data.default_combination.price);
    } else if (
      data?.combinations?.length &&
      data.combinations[0].price &&
      !isNaN(data?.combinations[0]?.price)
    ) {
      setPrice(data.combinations[0].price);
    } else if (data?.combination?.price && !isNaN(data?.combination?.price)) {
      setPrice(data.combination.price);
    }
  };

  const getProductTags = (data) => {
    if (data?.tags?.length) {
      setTags(data.tags);
    } else if (data?.default_combination?.tags?.length) {
      setTags(data.default_combination.tags);
    } else if (
      data?.combinations?.length &&
      data.combinations[0].tags?.length
    ) {
      setTags(data.combinations[0].tags);
    } else if (data?.combination?.tags?.length) {
      setTags(data.combination.tags);
    }
  };

  const convertData = (item) => {
    if (item._id) {
      item.id = item._id;
    }
    item.promotion_type = item.promotion_type
      ? promotionTypes[item.promotion_type]
      : '-';
    item.periode =
      item.from_date || item.to_date
        ? `${
            item.from_date
              ? parseDate(item.from_date, isWeb ? 'd MMMM yyyy' : 'd MMM yyyy')
              : '-'
          } - ${
            item.to_date
              ? parseDate(item.to_date, isWeb ? 'd MMMM yyyy' : 'd MMM yyyy')
              : '-'
          }`
        : '-';
    item.last_updated =
      item.updated_at || item.updated_by
        ? `${item.updated_at ? parseDate(item.updated_at) : '-'} | ${
            item?.updated_by?.name ? item.updated_by.name : '-'
          }`
        : '-';
    return item;
  };

  const getProductPromotions = async (id, combinationId) => {
    setLoadingPromotion(true);
    try {
      const response = await MsCatalog.getProductPromotions(id, combinationId);
      if (response?.data?.data?.length) {
        const result = response.data.data.map((item) => {
          return convertData(item);
        });
        setLoadingPromotion(false);
        setPromotions(result);
      } else {
        setLoadingPromotion(false);
        setPromotions([]);
      }
    } catch (error) {
      setLoadingPromotion(false);
      setPromotions([]);
    }
  };

  const changeCombination = (id = '', type = '') => {
    let combinations =
      detail.combinations && detail.combinations.length
        ? detail.combinations
        : [];
    if (!combinations.length) {
      if (
        detail.default_combination &&
        Object.keys(detail.default_combination).length
      ) {
        combinations = [detail.default_combination];
      } else if (detail.combination && Object.keys(detail.combination).length) {
        combinations = [detail.combination];
      }
    }
    if (combinations && combinations.length) {
      const combs = combinations;
      // const oldObj = JSON.parse(JSON.stringify(comObj));
      const lists = [];
      const listsObj = {
        size: [],
        shade: [],
        variant: [],
      };
      // Get Unique Attibutes
      for (let i = 0; i < combs.length; i++) {
        const com = combs[i];
        if (com && com.attributes && Object.keys(com.attributes).length) {
          const attrs = Object.keys(com.attributes);
          for (let j = 0; j < attrs.length; j++) {
            const attr = attrs[j];
            if (lists.indexOf(attr) == -1 && attr != 'non_specify') {
              lists.push(attr);
            }
          }
        }
      }
      // Set Attributes
      for (const comb of combs) {
        if (comb && comb.attributes && Object.keys(comb.attributes).length) {
          const attrs = comb.attributes;
          for (const key of lists) {
            if (listsObj[key].findIndex((a) => a.id == attrs[key].id) == -1) {
              listsObj[key].push({
                ...attrs[key],
                type: key,
                combination_id: comb._id,
                isActive: id == attrs[key].id,
              });
            }
          }
        }
      }
      setComObj(listsObj);
      setSizes(listsObj.size);
      setShades(listsObj.shade);
      setVariants(listsObj.variant);
      // Set Image Combination
      if (id && type) {
        setTimeout(() => {
          if (combs && combs.length) {
            const findData = combs.find((data) => {
              let result = false;
              const arr = [];
              const activeArr = [];
              for (let i = 0; i < lists.length; i++) {
                const key = lists[i];
                const idx = listsObj[key].findIndex((a) => a.isActive);
                if (idx > -1) {
                  activeArr.push(listsObj[key][idx]);
                }
              }
              if (activeArr.length == 1) {
                result = data._id == activeArr[0].combination_id;
              } else {
                for (let i = 0; i < lists.length; i++) {
                  const attr = lists[i];
                  if (data.attributes && Object.keys(data.attributes).length) {
                    const resultArr = activeArr.find((a) => a.type == attr);
                    if (
                      (resultArr && resultArr.id) == data.attributes[attr].id
                    ) {
                      arr.push(true);
                    }
                  }
                }
                if (arr.filter((a) => a).length == activeArr.length) {
                  result = true;
                }
              }
              return result;
            });
            if (findData) {
              if (findData?.reference) {
                setReference(findData.reference);
              } else {
                setReference('');
              }
              if (findData?.price) {
                if (!isNaN(findData.price)) {
                  setPrice(findData.price);
                } else {
                  setPrice(0);
                }
              } else {
                setPrice(0);
              }
              if (findData?.tags?.length) {
                setTags(findData.tags);
              } else {
                setTags([]);
              }
              if (findData?.images) {
                setSelectedImageCombination(getImageUrl(findData.images));
              } else if (findData?.image) {
                setSelectedImageCombination(getImageUrl([findData.image]));
              }
            }
          }
        }, 250);
      }
    }
  };

  const convertShadeValue = (shades) => {
    const find = shades.find((item) => item.isActive);
    return find ? find.name : '-';
  };

  const isHex = (value) => {
    if (value) {
      return /^#[0-9A-F]{6}$/i.test(value);
    }
    return false;
  };

  useEffect(() => {
    changeCombination();
  }, [productData]);

  return (
    <ModalFull
      title={isWeb ? '' : title ? title : detail.name ? detail.name : '-'}
      show={show}
      back={back}
      maxWidth={isWeb ? containerWidth : '100%'}
      onClose={() => onClose()}>
      <ScrollView style={styles.popupProductDetail}>
        {selectedImageCombination && selectedImageCombination.length ? (
          <View
            style={{
              width: '100%',
              marginBottom: 20,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <View style={styles.badgeContainer}>
              <View style={styles.badgeWrapper}>
                {tags?.indexOf('bestseller') > -1 ? (
                  <ProductBadge status="best" style={styles.productBadge} />
                ) : (
                  false
                )}
                {tags?.indexOf('new_arrival') > -1 ? (
                  <ProductBadge status="new" style={styles.productBadge} />
                ) : (
                  false
                )}
                {tags?.indexOf('promo') > -1 ? (
                  <ProductBadge status="promo" style={styles.productBadge} />
                ) : (
                  false
                )}
              </View>
            </View>
            <SliderImage
              data={selectedImageCombination}
              width={isWeb ? containerWidth : fullWidth}
              maxHeight={imagePopupSize}
              indicator
              renderItem={({ item, index }) => (
                <TouchableOpacity
                  style={{
                    width: containerWidth,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  key={index}
                  onPress={() =>
                    dispatch(showImage({ isModal: true, url: item }))
                  }>
                  <Image
                    style={{ width: imagePopupSize, height: imagePopupSize }}
                    width={imagePopupSize}
                    height={imagePopupSize}
                    source={item}
                    centered={true}
                  />
                </TouchableOpacity>
              )}
            />
          </View>
        ) : (
          false
        )}
        {(sizes && sizes.length) ||
        (variants && variants.length) ||
        (shades && shades.length) ? (
          <View style={{ width: '100%', marginBottom: 10 }}>
            {sizes && sizes.length ? (
              <View style={styles.combinationItem}>
                <View style={styles.combinationTitle}>
                  <SociollaText style={styles.combinationItemTitle}>
                    Size:
                  </SociollaText>
                </View>
                <FlatList
                  horizontal
                  data={sizes}
                  keyExtractor={(item, index) => `${item.id}-${index}`}
                  renderItem={({ item, index }) => (
                    <TouchableOpacity
                      activeOpacity={0.6}
                      style={[
                        styles.variantStyle,
                        index == 0 ? { marginLeft: 15 } : {},
                        item.isActive ? styles.variantActiveStyle : {},
                      ]}
                      onPress={() => changeCombination(item.id, 'size')}>
                      <SociollaText style={styles.variantTextStyle}>
                        {item.name}
                      </SociollaText>
                    </TouchableOpacity>
                  )}
                  showsHorizontalScrollIndicator={false}
                />
              </View>
            ) : (
              false
            )}
            {variants && variants.length ? (
              <View style={styles.combinationItem}>
                <View style={styles.combinationTitle}>
                  <SociollaText style={styles.combinationItemTitle}>
                    Variant:
                  </SociollaText>
                </View>
                <FlatList
                  horizontal
                  data={variants}
                  keyExtractor={(item, index) => `${item.id}-${index}`}
                  renderItem={({ item, index }) => (
                    <TouchableOpacity
                      activeOpacity={0.6}
                      style={[
                        styles.variantStyle,
                        index == 0 ? { marginLeft: 15 } : {},
                        item.isActive ? styles.variantActiveStyle : {},
                      ]}
                      onPress={() => changeCombination(item.id, 'variant')}>
                      <SociollaText style={styles.variantTextStyle}>
                        {item.name}
                      </SociollaText>
                    </TouchableOpacity>
                  )}
                  showsHorizontalScrollIndicator={false}
                />
              </View>
            ) : (
              false
            )}
            {shades && shades.length ? (
              <View style={styles.combinationItem}>
                <View style={styles.combinationTitle}>
                  <SociollaText style={styles.combinationItemTitle}>
                    Shade:
                  </SociollaText>
                  <SociollaText
                    style={[styles.combinationItemTitle, { marginLeft: 5 }]}
                    type="medium">
                    {convertShadeValue(shades)}
                  </SociollaText>
                </View>
                <FlatList
                  horizontal
                  data={shades}
                  keyExtractor={(item, index) => `${item.id}-${index}`}
                  renderItem={({ item, index }) => (
                    <TouchableOpacity
                      activeOpacity={0.6}
                      style={[
                        styles.shadeStyle,
                        index == 0 ? { marginLeft: 15 } : {},
                        item.isActive ? styles.shadeActiveStyle : {},
                      ]}
                      onPress={() => changeCombination(item.id, 'shade')}>
                      {item.value ? (
                        isHex(item.value) ? (
                          <View
                            style={{
                              width: 19,
                              height: 19,
                              borderRadius: 22,
                              backgroundColor: item.value,
                            }}></View>
                        ) : item.value.indexOf('http') > -1 ? (
                          <View
                            style={{
                              width: 19,
                              height: 19,
                              borderRadius: 22,
                              backgroundColor: Colors.white,
                              overflow: 'hidden',
                            }}>
                            <Image
                              style={{
                                width: 19,
                                height: 19,
                                borderRadius: 22,
                              }}
                              width={18}
                              height={18}
                              source={item.value}
                            />
                          </View>
                        ) : (
                          <View
                            style={{
                              width: 19,
                              height: 19,
                              borderRadius: 22,
                              backgroundColor: Colors.white,
                            }}></View>
                        )
                      ) : (
                        <View
                          style={{
                            width: 19,
                            height: 19,
                            borderRadius: 22,
                            backgroundColor: Colors.white,
                          }}></View>
                      )}
                    </TouchableOpacity>
                  )}
                  showsHorizontalScrollIndicator={false}
                />
              </View>
            ) : (
              false
            )}
          </View>
        ) : (
          false
        )}
        <View style={{ paddingHorizontal: 15 }}>
          <SociollaText style={styles.productReference} type="medium">
            {reference ? `[${reference}]` : '-'}
          </SociollaText>
          <SociollaText style={styles.productName}>
            {detail.brand && detail.brand.name ? `${detail.brand.name} - ` : ''}
            {detail.name ? detail.name : '-'}
          </SociollaText>
          {price ? (
            <SociollaText style={styles.productName}>
              {currency(price, true)}
            </SociollaText>
          ) : (
            false
          )}
          {!isWeb && detail.default_category?.name ? (
            <SociollaText style={styles.productCategory}>
              {detail.default_category.name}
            </SociollaText>
          ) : (
            false
          )}
        </View>
        <TitleSubSection title="INTERNAL NOTES" />
        <View style={{ paddingHorizontal: 15 }}>
          {!isWeb && (
            <>
              <ItemDetail name="Brand" value={detail.brand?.name || '-'} />
              <ItemDetail name="Category" value={detail.categoryPath || '-'} />
            </>
          )}
          <ItemDetail
            name="Description"
            html={true}
            value={detail.description ? detail.description : '-'}
          />
          <ItemDetail
            name="How to Use"
            html={true}
            value={detail.how_to_use ? detail.how_to_use : ''}
          />
          <ItemDetail
            name="Ingredients"
            html={true}
            value={detail.ingredients ? detail.ingredients : ''}
          />
        </View>
        {withPromotion ? (
          <>
            <TitleSubSection title="PROMOTION RULES" />
            <View style={styles.promotionContainer}>
              {loadingPromotion ? (
                <View style={styles.noData}>
                  <Loader />
                </View>
              ) : promotions?.length ? (
                promotions.map((item, idx) => (
                  <View style={styles.promotionItem} key={idx}>
                    <View
                      style={[
                        styles.promotionItemSection,
                        { marginBottom: 10 },
                      ]}>
                      <SociollaText
                        type="medium"
                        style={{
                          width: isNative
                            ? fullWidth - 130
                            : 'calc(100% - 105px)',
                          fontSize: 15,
                        }}>
                        {item.name ? item.name : '-'}
                      </SociollaText>
                      <View style={styles.statusBadge}>
                        <StatusBadge
                          status={item.is_active ? 'active' : 'in_active'}
                        />
                      </View>
                    </View>
                    <View style={styles.promotionItemSection}>
                      <View
                        style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Image
                          style={{
                            width: 19,
                            height: 14,
                            marginLeft: -5,
                            marginRight: 5,
                          }}
                          width={19}
                          height={14}
                          svg={true}
                          source={require('@assets/img/icons/menu/calendar.svg')}
                        />
                        <SociollaText
                          style={{ fontSize: 11, color: Colors.grey2 }}>
                          {item.periode ? item.periode : '-'}
                        </SociollaText>
                      </View>
                      <SociollaText
                        style={{ fontSize: 11, color: Colors.grey1 }}>
                        Type: {item.promotion_type}
                      </SociollaText>
                    </View>
                  </View>
                ))
              ) : (
                <View style={styles.noData}>
                  <SociollaText>No promotion found</SociollaText>
                </View>
              )}
            </View>
          </>
        ) : (
          false
        )}
        {isNative ? <View style={styles.spacer} /> : false}
      </ScrollView>
      <ModalImage />
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  popupProductDetail: {
    width: '100%',
    paddingTop: isWeb ? 0 : 20,
    maxHeight: isMobileWeb
      ? 'auto'
      : isWeb
      ? fullHeight - 200
      : fullHeight - 55,
  },
  productName: {
    fontSize: 13,
    marginBottom: 10,
  },
  productCategory: {
    fontSize: 12,
    marginBottom: 10,
    color: Colors.grey2,
  },
  productReference: {
    fontSize: 15,
    color: Colors.black5,
    marginBottom: 15,
  },
  combinationItem: { width: '100%', paddingVertical: 5 },
  combinationTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 15,
  },
  combinationItemTitle: { fontSize: 12, marginBottom: 5, color: Colors.grey1 },
  variantStyle: {
    paddingVertical: 5,
    paddingHorizontal: 15,
    marginRight: 10,
    borderRadius: 30,
    borderColor: Colors.grey0,
    borderWidth: 1,
  },
  variantActiveStyle: {
    borderColor: Colors.blue6,
    backgroundColor: Colors.blue6,
  },
  variantTextStyle: { fontSize: 12 },
  shadeStyle: {
    width: 22,
    height: 22,
    paddingVertical: 5,
    marginRight: 8,
    backgroundColor: Colors.grey0,
    borderColor: Colors.grey0,
    borderWidth: 2,
    borderRadius: 22,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shadeActiveStyle: { borderColor: Colors.blue1, borderWidth: 2 },
  productBadge: { marginLeft: 5, marginBottom: 5 },
  badgeContainer: {
    position: 'absolute',
    top: 0,
    right: 15,
    zIndex: 1,
    elevation: 1,
  },
  badgeWrapper: {
    maxWidth: 130,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  promotionContainer: { width: '100%' },
  noData: { padding: 15 },
  promotionItem: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  promotionItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  spacer: {
    width: '100%',
    height: 55,
  },
  statusBadge: {
    width: 105,
  },
});

ModalDetailProduct.propTypes = propTypes;
ModalDetailProduct.defaultProps = defaultProps;

export default React.memo(ModalDetailProduct, (prev, next) => prev == next);
